<template>
  <div class="w-full">
    <div
      v-if="uploading"
      class="flex min-h-66 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-royal-200 bg-royal-25 p-8"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <EcxSpinner size="md" variant="primary" />
      <EcxText size="sm" weight="medium" color="primary">{{ t('corp.bulk_import.upload.uploading') }}</EcxText>
    </div>

    <div
      v-else-if="file"
      class="flex min-h-66 flex-col items-center justify-center gap-5 rounded-2xl border border-royal-500 bg-royal-25 p-5"
    >
      <span class="relative inline-block">
        <img :src="folderSpreadsheet" alt="" class="w-18 shrink-0" />
        <CheckFilledIcon class="absolute bottom-0 right-0 h-5 w-5 text-green-500" />
      </span>
      <div class="flex flex-col items-center gap-1 text-center">
        <EcxText size="base" weight="normal" color="primary">
          {{ t('corp.bulk_import.upload.attached_title') }}
        </EcxText>
        <EcxText size="sm" color="secondary">{{ file.name }} &bull; {{ formatBytes(file.size) }}</EcxText>
      </div>
      <EcxButton
        variant="outline"
        size="sm"
        data-testid="replace-btn"
        @click="emit('replace')"
      >
        <template #iconLeft>
          <RefreshIcon class="h-5 w-5" />
        </template>
        {{ t('corp.bulk_import.upload.replace_file') }}
      </EcxButton>
    </div>

    <div
      v-else-if="displayedError"
      data-testid="dropzone-error"
      role="alert"
      class="flex min-h-66 flex-col items-center justify-center gap-5 rounded-2xl border border-red-500 bg-red-25 p-5 text-center"
    >
      <img :src="folderSpreadsheetError" alt="" class="w-18 shrink-0" />
      <div class="flex flex-col items-center gap-2">
        <EcxText size="base" weight="normal" class="text-red-500">
          <span v-if="displayedError === 'format'">{{ t('corp.bulk_import.upload.error_format_title') }}</span>
          <span v-else-if="displayedError === 'row_limit'">{{ t('corp.bulk_import.upload.error_row_limit_title') }}</span>
          <span v-else>{{ t('corp.bulk_import.upload.error_size_title') }}</span>
        </EcxText>
        <EcxText size="sm" color="secondary">
          <span v-if="displayedError === 'format'">{{ t('corp.bulk_import.upload.error_format_hint') }}</span>
          <span v-else-if="displayedError === 'row_limit'">{{ t('corp.bulk_import.upload.error_row_limit_hint') }}</span>
          <span v-else>{{ t('corp.bulk_import.upload.error_size_hint', { size: formatBytes(maxSizeMb * 1024 * 1024) }) }}</span>
        </EcxText>
        <EcxButton
          variant="outline"
          size="sm"
          data-testid="replace-btn"
          @click="handleErrorReplace"
        >
          <template #iconLeft>
            <RefreshIcon class="h-5 w-5" />
          </template>
          {{ t('corp.bulk_import.upload.replace_file') }}
        </EcxButton>
      </div>
    </div>

    <div
      v-else
      data-testid="dropzone"
      role="button"
      tabindex="0"
      :aria-label="t('corp.bulk_import.upload.dropzone_title')"
      class="flex min-h-66 cursor-pointer flex-col items-center justify-center gap-5 rounded-lg border border-dashed p-5 text-center transition-colors duration-200"
      :class="isDragging
        ? 'border-primary bg-royal-50 shadow-none'
        : 'border-royal-500 bg-royal-25 hover:border-royal-400 hover:bg-royal-50 shadow-bulk-import-dropzone'"
      @click="triggerFileInput"
      @keydown.enter.prevent="triggerFileInput"
      @keydown.space.prevent="triggerFileInput"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInputRef"
        type="file"
        :accept="accept"
        :aria-label="t('corp.bulk_import.upload.dropzone_title')"
        class="hidden"
        @change="handleFileChange"
      />
      <img :src="folderSpreadsheet" alt="" class="w-18 shrink-0" />
      <div class="flex flex-col gap-1">
        <EcxText size="sm" weight="normal" color="default">
          {{ t('corp.bulk_import.upload.dropzone_title') }}
        </EcxText>
        <EcxText size="sm" color="secondary">
          {{ t('corp.bulk_import.upload.dropzone_hint') }}
        </EcxText>
        <EcxText size="sm" class="text-royal-500">
          {{ t('corp.bulk_import.upload.supported_formats_dot') }}
        </EcxText>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { EcxButton, EcxSpinner, EcxText } from '@ecx/ui'
import { RefreshIcon, CheckFilledIcon } from '@ecx/ui/icons'
import folderSpreadsheet from '@/assets/bulkImport/folder-spreadsheet.svg'
import folderSpreadsheetError from '@/assets/bulkImport/folder-spreadsheet-error.svg'

const props = withDefaults(
  defineProps<{
    file: File | null
    uploading?: boolean
    maxSizeMb?: number
    accept?: string
    initialError?: 'format' | 'size' | null
    serverError?: 'row_limit' | null
  }>(),
  {
    uploading: false,
    maxSizeMb: 20,
    accept: '.xlsx,.csv',
    initialError: null,
    serverError: null,
  },
)

const emit = defineEmits<{
  (e: 'file-selected', file: File): void
  (e: 'replace'): void
  (e: 'invalid', reason: 'format' | 'size'): void
}>()

const { t } = useI18n()

const fileInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const errorReason = ref<'format' | 'size' | null>(props.initialError ?? null)

const displayedError = computed<'format' | 'size' | 'row_limit' | null>(
  () => errorReason.value ?? props.serverError,
)

function triggerFileInput() {
  fileInputRef.value?.click()
}

function clearError() {
  errorReason.value = null
}

function handleErrorReplace() {
  clearError()
  emit('replace')
}

function getAllowedExtensions(): string[] {
  return props.accept.split(',').map((ext) => ext.trim().toLowerCase())
}

function isValidExtension(filename: string): boolean {
  const lower = filename.toLowerCase()
  return getAllowedExtensions().some((ext) => lower.endsWith(ext))
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(0)} MB`
  }
  return `${(bytes / 1024).toFixed(0)} KB`
}

function processFile(file: File) {
  if (!isValidExtension(file.name)) {
    errorReason.value = 'format'
    emit('invalid', 'format')
    return
  }
  if (file.size > props.maxSizeMb * 1024 * 1024) {
    errorReason.value = 'size'
    emit('invalid', 'size')
    return
  }
  errorReason.value = null
  emit('file-selected', file)
}

function handleFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    processFile(file)
  }
  // reset input so re-selecting the same file triggers change
  input.value = ''
}

function handleDrop(event: DragEvent) {
  isDragging.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) {
    processFile(file)
  }
}
</script>
